import { useEffect, useMemo, useReducer, useState, type ReactNode } from "react";
import { CoreBus } from "../core/bus";
import { RoomConnection } from "../core/room-connection";
import { PeersManager } from "../core/peers-manager";
import { RTCPeer } from "../core/rtc-peer";
import { generateDisplayName, deviceNameFromUA } from "../lib/display-name";
import { isIOS } from "../core/environment";
import { getThumbnailAsDataUrl } from "../core/thumbnail";
import { peersReducer, type PeersState, type RoomPeer } from "./peer-state";
import { generateRoomId, roomLinkFor } from "./room-id";
import { RoomContext, type RoomContextValue } from "./RoomContext";

const EDITED_NAME_KEY = "edited_display_name";

function getPeerId(): string {
  const existing = sessionStorage.getItem("peer_id");
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem("peer_id", id);
  return id;
}

type CoreServices = {
  bus: CoreBus;
  conn: RoomConnection;
  manager: PeersManager;
  peerId: string;
  generatedName: string;
  rtcPeers: Map<string, RTCPeer>;
};

function buildCore(): CoreServices {
  const bus = new CoreBus();
  const peerId = getPeerId();
  const generatedName = generateDisplayName(peerId);
  const deviceName = deviceNameFromUA(navigator.userAgent);
  const rtcPeers = new Map<string, RTCPeer>();

  const conn = new RoomConnection({
    bus,
    identity: { peerId, displayName: generatedName, deviceName },
    fetchFn: (input, init) => fetch(input, init),
    sendBeaconFn: (url, body) => navigator.sendBeacon(url, body),
  });

  const manager = new PeersManager({
    bus,
    selfPeerId: peerId,
    createPeer: (isCaller, remotePeerId) => {
      const peer = new RTCPeer({
        bus,
        peerId: remotePeerId,
        isCaller,
        isIOS: isIOS(navigator.userAgent),
        getThumbnail: getThumbnailAsDataUrl,
        rtcConfig: { iceServers: conn.iceServers() },
      });
      rtcPeers.set(remotePeerId, peer);
      return peer;
    },
    onPeerRemoved: (removedId) => {
      const peer = rtcPeers.get(removedId);
      peer?.disconnect();
      peer?.conn?.close();
      rtcPeers.delete(removedId);
    },
  });

  return { bus, conn, manager, peerId, generatedName, rtcPeers };
}

export function RoomProvider({ children }: { children: ReactNode }) {
  const [roomId, setRoomId] = useState<string | null>(null);
  const [peersState, dispatch] = useReducer(peersReducer, {} as PeersState);
  const [editedName, setEditedNameState] = useState<string>(() => localStorage.getItem(EDITED_NAME_KEY) ?? "");

  // Built once via lazy useState initializer: identity, bus, and core services with real browser deps.
  const [{ bus, conn, peerId, generatedName }] = useState<CoreServices>(buildCore);

  const join = (id: string) => { setRoomId(id); conn.joinRoom(id); };

  // Subscribe React state to bus peer events.
  useEffect(() => {
    const offs = [
      bus.on("peer-joined", (d) => dispatch({ kind: "joined", peer: d.peer })),
      bus.on("peer-connected", (d) => dispatch({ kind: "connected", peerId: d.peerId, connectionHash: d.connectionHash })),
      bus.on("peer-disconnected", (id) => dispatch({ kind: "disconnected", peerId: id })),
      bus.on("peer-display-name-changed", (d) => dispatch({ kind: "renamed", peerId: d.peerId, displayName: d.displayName })),
    ];
    return () => offs.forEach((off) => off());
  }, [bus]);

  // Browser lifecycle: leaving the page leaves the room and closes connections.
  useEffect(() => {
    const onPageHide = () => bus.emit("pagehide", undefined);
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [bus]);

  // Load server config, announce our identity, then honor a ?room= deep link.
  useEffect(() => {
    let cancelled = false;
    conn.loadConfig().then(() => {
      if (cancelled) return;
      bus.emit("display-name", { peerId, displayName: generatedName, deviceName: deviceNameFromUA(navigator.userAgent) });
      const deepLink = new URLSearchParams(window.location.search).get("room");
      if (deepLink) join(deepLink.trim());
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Propagate the user's chosen name to peers whenever it changes.
  useEffect(() => {
    bus.emit("self-display-name-changed", editedName);
  }, [bus, editedName]);

  const value = useMemo<RoomContextValue>(() => {
    const peers: RoomPeer[] = Object.values(peersState);
    return {
      bus,
      roomId,
      peers,
      generatedName,
      editedName,
      createRoom: () => join(generateRoomId()),
      joinRoom: (id: string) => join(id),
      leaveRoom: () => { conn.leaveRoom(); setRoomId(null); },
      copyLink: () => {
        if (!roomId) return;
        navigator.clipboard.writeText(roomLinkFor(roomId, window.location.href))
          .then(() => bus.emit("notify-user", "Room link copied to clipboard"))
          .catch(() => bus.emit("notify-user", "Copying not possible. Copy manually."));
      },
      setEditedName: (name: string) => {
        const trimmed = name.replace(/(\n|\r|\r\n)/g, "");
        if (trimmed) localStorage.setItem(EDITED_NAME_KEY, trimmed);
        else localStorage.removeItem(EDITED_NAME_KEY);
        setEditedNameState(trimmed);
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus, roomId, peersState, generatedName, editedName]);

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}
